import { useEffect, useState } from 'react';
import { API_BASE, api } from '@shared/api';

export type Lang = 'curl' | 'python' | 'node' | 'php';

export const LANGS: ReadonlyArray<{ id: Lang; label: string }> = [
  { id: 'curl', label: 'cURL' },
  { id: 'python', label: 'Python' },
  { id: 'node', label: 'Node.js' },
  { id: 'php', label: 'PHP' },
];

/** Anthropic SDKs add /v1. */
export const API_ORIGIN = API_BASE.replace(/\/$/, '');
export const BASE_URL = `${API_ORIGIN}/v1`;

/** Example when no catalog. */
export const DEFAULT_MODEL = 'claude-sonnet-5-5';

interface SnippetOptions {
  /** Omitted: uses env var */
  apiKey?: string;
  model?: string;
  prompt?: string;
  stream?: boolean;
}

const ENV = 'PARTNERAI_API_KEY';

export const API_KEY_ENV = ENV;

export function chatSnippet(lang: Lang, options: SnippetOptions = {}): string {
  const model = options.model ?? DEFAULT_MODEL;
  const prompt = (options.prompt ?? 'Halo! Jelaskan apa itu API dalam satu kalimat.').replace(/"/g, '\\"');
  const stream = Boolean(options.stream);
  const key = options.apiKey;

  switch (lang) {
    case 'curl':
      return [
        `curl ${stream ? '-N ' : ''}${BASE_URL}/chat/completions \\`,
        `  -H "Authorization: Bearer ${key ?? `$${ENV}`}" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -d '{`,
        `    "model": "${model}",`,
        `    "messages": [{"role": "user", "content": "${prompt.replace(/'/g, "'\\''")}"}]${stream ? ',' : ''}`,
        ...(stream ? [`    "stream": true`] : []),
        `  }'`,
      ].join('\n');

    case 'python':
      return [
        '# pip install openai',
        ...(key ? [] : ['import os']),
        'from openai import OpenAI',
        '',
        'client = OpenAI(',
        `    base_url="${BASE_URL}",`,
        `    api_key=${key ? `"${key}"` : `os.environ["${ENV}"]`},`,
        ')',
        '',
        ...(stream
          ? [
              'stream = client.chat.completions.create(',
              `    model="${model}",`,
              `    messages=[{"role": "user", "content": "${prompt}"}],`,
              '    stream=True,',
              ')',
              'for chunk in stream:',
              '    if chunk.choices:',
              '        print(chunk.choices[0].delta.content or "", end="")',
            ]
          : [
              'response = client.chat.completions.create(',
              `    model="${model}",`,
              `    messages=[{"role": "user", "content": "${prompt}"}],`,
              ')',
              'print(response.choices[0].message.content)',
            ]),
      ].join('\n');

    case 'node':
      return [
        '// npm install openai',
        'import OpenAI from "openai";',
        '',
        'const client = new OpenAI({',
        `  baseURL: "${BASE_URL}",`,
        `  apiKey: ${key ? `"${key}"` : `process.env.${ENV}`},`,
        '});',
        '',
        ...(stream
          ? [
              'const stream = await client.chat.completions.create({',
              `  model: "${model}",`,
              `  messages: [{ role: "user", content: "${prompt}" }],`,
              '  stream: true,',
              '});',
              'for await (const chunk of stream) {',
              '  process.stdout.write(chunk.choices[0]?.delta?.content ?? "");',
              '}',
            ]
          : [
              'const response = await client.chat.completions.create({',
              `  model: "${model}",`,
              `  messages: [{ role: "user", content: "${prompt}" }],`,
              '});',
              'console.log(response.choices[0].message.content);',
            ]),
      ].join('\n');

    case 'php':
      return [
        '<?php',
        '// composer require openai-php/client guzzlehttp/guzzle',
        "require 'vendor/autoload.php';",
        '',
        '$client = OpenAI::factory()',
        `    ->withApiKey(${key ? `'${key}'` : `getenv('${ENV}')`})`,
        `    ->withBaseUri('${BASE_URL}')`,
        '    ->make();',
        '',
        ...(stream
          ? [
              '$stream = $client->chat()->createStreamed([',
              `    'model' => '${model}',`,
              `    'messages' => [['role' => 'user', 'content' => "${prompt}"]],`,
              ']);',
              'foreach ($stream as $chunk) {',
              '    echo $chunk->choices[0]->delta->content ?? \'\';',
              '}',
            ]
          : [
              '$response = $client->chat()->create([',
              `    'model' => '${model}',`,
              `    'messages' => [['role' => 'user', 'content' => "${prompt}"]],`,
              ']);',
              'echo $response->choices[0]->message->content;',
            ]),
      ].join('\n');
  }
}

export const DEFAULT_IMAGE_MODEL = 'seedream-5.0-flash';
export const DEFAULT_VIDEO_MODEL = 'seedance-2.0';

/** Video: no SDK helper */
export const VIDEO_LANGS: ReadonlyArray<{ id: Lang; label: string }> = LANGS.filter((l) => l.id !== 'php');

export function imageSnippet(lang: Lang, options: { apiKey?: string; model?: string; prompt?: string } = {}): string {
  const model = options.model ?? DEFAULT_IMAGE_MODEL;
  const prompt = (options.prompt ?? 'Poster kopi susu gula aren, gaya ilustrasi flat, warna hangat').replace(/"/g, '\\"');
  const key = options.apiKey;
  switch (lang) {
    case 'curl':
      return [
        `curl ${BASE_URL}/images/generations \\`,
        `  -H "Authorization: Bearer ${key ?? `$${ENV}`}" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -d '{"model": "${model}", "prompt": "${prompt}", "n": 1}'`,
      ].join('\n');
    case 'python':
      return [
        '# pip install openai',
        ...(key ? [] : ['import os']),
        'from openai import OpenAI',
        '',
        `client = OpenAI(base_url="${BASE_URL}", api_key=${key ? `"${key}"` : `os.environ["${ENV}"]`})`,
        '',
        `result = client.images.generate(model="${model}", prompt="${prompt}", n=1)`,
        'print(result.data[0].url)',
      ].join('\n');
    case 'node':
      return [
        '// npm install openai',
        'import OpenAI from "openai";',
        '',
        `const client = new OpenAI({ baseURL: "${BASE_URL}", apiKey: ${key ? `"${key}"` : `process.env.${ENV}`} });`,
        '',
        `const result = await client.images.generate({ model: "${model}", prompt: "${prompt}", n: 1 });`,
        'console.log(result.data[0].url);',
      ].join('\n');
    case 'php':
      return [
        '<?php',
        "require 'vendor/autoload.php';",
        '',
        `$client = OpenAI::factory()->withApiKey(${key ? `'${key}'` : `getenv('${ENV}')`})->withBaseUri('${BASE_URL}')->make();`,
        '',
        `$result = $client->images()->create(['model' => '${model}', 'prompt' => "${prompt}", 'n' => 1]);`,
        'echo $result->data[0]->url;',
      ].join('\n');
  }
}

export function videoSnippet(lang: Lang, options: { apiKey?: string; model?: string; prompt?: string } = {}): string {
  const model = options.model ?? DEFAULT_VIDEO_MODEL;
  const prompt = (options.prompt ?? 'Ombak di Pantai Kuta saat senja, kamera bergerak pelan').replace(/"/g, '\\"');
  const key = options.apiKey;
  switch (lang) {
    case 'python':
      return [
        '# pip install requests',
        'import os, time, requests',
        '',
        `BASE = "${BASE_URL}"`,
        `headers = {"Authorization": "Bearer " + ${key ? `"${key}"` : `os.environ["${ENV}"]`}}`,
        '',
        '# 1. buat video',
        'video = requests.post(f"{BASE}/videos", headers=headers, json={',
        `    "model": "${model}",`,
        `    "prompt": "${prompt}",`,
        '    "seconds": 5,',
        '    "resolution": "720p",',
        '}).json()',
        '',
        '# 2. tunggu sampai selesai',
        'while video["status"] in ("queued", "in_progress"):',
        '    time.sleep(5)',
        '    video = requests.get(f"{BASE}/videos/{video[\'id\']}", headers=headers).json()',
        '',
        '# 3. unduh (link berlaku 24 jam)',
        'if video["status"] == "completed":',
        '    data = requests.get(video["url"], headers=headers).content',
        '    open("video.mp4", "wb").write(data)',
        'print(video["status"], video.get("billing"))',
      ].join('\n');
    case 'node':
      return [
        `const BASE = "${BASE_URL}";`,
        `const headers = { Authorization: \`Bearer \${${key ? `"${key}"` : `process.env.${ENV}`}}\`, "Content-Type": "application/json" };`,
        '',
        '// 1. buat video',
        'let video = await (await fetch(`${BASE}/videos`, {',
        '  method: "POST",',
        '  headers,',
        `  body: JSON.stringify({ model: "${model}", prompt: "${prompt}", seconds: 5, resolution: "720p" }),`,
        '})).json();',
        '',
        '// 2. tunggu sampai selesai',
        'while (["queued", "in_progress"].includes(video.status)) {',
        '  await new Promise((r) => setTimeout(r, 5000));',
        '  video = await (await fetch(`${BASE}/videos/${video.id}`, { headers })).json();',
        '}',
        'console.log(video.status, video.url);',
      ].join('\n');
    default:
      return [
        '# 1. buat video',
        `curl ${BASE_URL}/videos \\`,
        `  -H "Authorization: Bearer ${key ?? `$${ENV}`}" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -d '{"model": "${model}", "prompt": "${prompt}", "seconds": 5, "resolution": "720p"}'`,
        '',
        '# 2. cek status (ulangi sampai "completed")',
        `curl ${BASE_URL}/videos/VIDEO_ID -H "Authorization: Bearer ${key ?? `$${ENV}`}"`,
        '',
        '# 3. unduh',
        `curl -L ${BASE_URL}/videos/VIDEO_ID/content -H "Authorization: Bearer ${key ?? `$${ENV}`}" -o video.mp4`,
      ].join('\n');
  }
}

/** Callable model for examples. */
export function useExampleModel(enabled = true, modality: 'text' | 'image' | 'video' = 'text'): string {
  const fallback = modality === 'image' ? DEFAULT_IMAGE_MODEL : modality === 'video' ? DEFAULT_VIDEO_MODEL : DEFAULT_MODEL;
  const [model, setModel] = useState(fallback);
  useEffect(() => {
    if (!enabled) return;
    api<{ data: Array<{ id: string; owned_by: string; modality?: string }> }>('/v1/models')
      .then((catalog) => {
        const ofKind = catalog.data.filter((m) => (m.modality ?? 'text') === modality);
        const pick =
          ofKind.find((m) => m.id === fallback) ??
          ofKind.find((m) => m.owned_by !== 'mock') ??
          ofKind.find((m) => m.id === 'mock-fast') ??
          ofKind[0];
        if (pick) setModel(pick.id);
      })
      .catch(() => undefined);
  }, [enabled, modality, fallback]);
  return model;
}

/* Docs-only generators below. */

interface DocSnippetOptions {
  apiKey?: string;
  model?: string;
}

function clientHeader(lang: 'python' | 'node' | 'php', key?: string, pyModules: string[] = []): string[] {
  if (lang === 'python') {
    const modules = [...(key ? [] : ['os']), ...pyModules];
    return [
      '# pip install openai',
      ...(modules.length ? [`import ${modules.join(', ')}`] : []),
      'from openai import OpenAI',
      '',
      `client = OpenAI(base_url="${BASE_URL}", api_key=${key ? `"${key}"` : `os.environ["${ENV}"]`})`,
      '',
    ];
  }
  if (lang === 'node') {
    return [
      '// npm install openai',
      'import OpenAI from "openai";',
      '',
      `const client = new OpenAI({ baseURL: "${BASE_URL}", apiKey: ${key ? `"${key}"` : `process.env.${ENV}`} });`,
      '',
    ];
  }
  return [
    '<?php',
    "require 'vendor/autoload.php';",
    '',
    `$client = OpenAI::factory()->withApiKey(${key ? `'${key}'` : `getenv('${ENV}')`})->withBaseUri('${BASE_URL}')->make();`,
    '',
  ];
}

/** Final chunk carries usage. */
export function streamSnippet(lang: Lang, options: DocSnippetOptions = {}): string {
  const model = options.model ?? DEFAULT_MODEL;
  const prompt = 'Tulis tiga baris puisi tentang hujan di Bogor.';
  const key = options.apiKey;
  switch (lang) {
    case 'curl':
      return [
        `curl -N ${BASE_URL}/chat/completions \\`,
        `  -H "Authorization: Bearer ${key ?? `$${ENV}`}" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -d '{`,
        `    "model": "${model}",`,
        `    "messages": [{"role": "user", "content": "${prompt}"}],`,
        `    "stream": true,`,
        `    "stream_options": {"include_usage": true}`,
        `  }'`,
      ].join('\n');
    case 'python':
      return [
        ...clientHeader('python', key),
        'stream = client.chat.completions.create(',
        `    model="${model}",`,
        `    messages=[{"role": "user", "content": "${prompt}"}],`,
        '    stream=True,',
        '    stream_options={"include_usage": True},',
        ')',
        'for chunk in stream:',
        '    if chunk.choices:',
        '        print(chunk.choices[0].delta.content or "", end="", flush=True)',
        '    elif chunk.usage:',
        '        # chunk terakhir: choices kosong, berisi usage',
        '        print("\\n", chunk.usage.total_tokens, "token")',
      ].join('\n');
    case 'node':
      return [
        ...clientHeader('node', key),
        'const stream = await client.chat.completions.create({',
        `  model: "${model}",`,
        `  messages: [{ role: "user", content: "${prompt}" }],`,
        '  stream: true,',
        '  stream_options: { include_usage: true },',
        '});',
        'for await (const chunk of stream) {',
        '  const delta = chunk.choices[0]?.delta?.content;',
        '  if (delta) process.stdout.write(delta);',
        '  // chunk terakhir: usage + billing',
        '  if (chunk.usage) console.log("\\n", chunk.usage, chunk.billing);',
        '}',
      ].join('\n');
    case 'php':
      return [
        ...clientHeader('php', key),
        '$stream = $client->chat()->createStreamed([',
        `    'model' => '${model}',`,
        `    'messages' => [['role' => 'user', 'content' => '${prompt}']],`,
        "    'stream_options' => ['include_usage' => true],",
        ']);',
        'foreach ($stream as $chunk) {',
        '    if (isset($chunk->choices[0])) {',
        "        echo $chunk->choices[0]->delta->content ?? '';",
        '    }',
        '}',
      ].join('\n');
  }
}

/** Tool calling round trip. */
export function toolsSnippet(lang: Lang, options: DocSnippetOptions = {}): string {
  const model = options.model ?? DEFAULT_MODEL;
  const key = options.apiKey;
  const question = 'Berapa ongkir 2 kg dari Bandung ke Medan?';
  switch (lang) {
    case 'curl':
      return [
        `curl ${BASE_URL}/chat/completions \\`,
        `  -H "Authorization: Bearer ${key ?? `$${ENV}`}" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -d '{`,
        `    "model": "${model}",`,
        `    "messages": [{"role": "user", "content": "${question}"}],`,
        `    "tools": [{`,
        `      "type": "function",`,
        `      "function": {`,
        `        "name": "cek_ongkir",`,
        `        "description": "Hitung ongkos kirim antar kota",`,
        `        "parameters": {`,
        `          "type": "object",`,
        `          "properties": {`,
        `            "asal": {"type": "string"},`,
        `            "tujuan": {"type": "string"},`,
        `            "berat_kg": {"type": "number"}`,
        `          },`,
        `          "required": ["asal", "tujuan", "berat_kg"]`,
        `        }`,
        `      }`,
        `    }]`,
        `  }'`,
      ].join('\n');
    case 'python':
      return [
        ...clientHeader('python', key, ['json']),
        'tools = [{',
        '    "type": "function",',
        '    "function": {',
        '        "name": "cek_ongkir",',
        '        "description": "Hitung ongkos kirim antar kota",',
        '        "parameters": {',
        '            "type": "object",',
        '            "properties": {',
        '                "asal": {"type": "string"},',
        '                "tujuan": {"type": "string"},',
        '                "berat_kg": {"type": "number"},',
        '            },',
        '            "required": ["asal", "tujuan", "berat_kg"],',
        '        },',
        '    },',
        '}]',
        '',
        `messages = [{"role": "user", "content": "${question}"}]`,
        `response = client.chat.completions.create(model="${model}", messages=messages, tools=tools)`,
        'message = response.choices[0].message',
        '',
        'if message.tool_calls:',
        '    call = message.tool_calls[0]',
        '    hasil = cek_ongkir(**json.loads(call.function.arguments))  # fungsi Anda',
        '    messages.append(message)',
        '    messages.append({"role": "tool", "tool_call_id": call.id, "content": json.dumps(hasil)})',
        `    final = client.chat.completions.create(model="${model}", messages=messages, tools=tools)`,
        '    print(final.choices[0].message.content)',
      ].join('\n');
    case 'node':
      return [
        ...clientHeader('node', key),
        'const tools = [{',
        '  type: "function",',
        '  function: {',
        '    name: "cek_ongkir",',
        '    description: "Hitung ongkos kirim antar kota",',
        '    parameters: {',
        '      type: "object",',
        '      properties: { asal: { type: "string" }, tujuan: { type: "string" }, berat_kg: { type: "number" } },',
        '      required: ["asal", "tujuan", "berat_kg"],',
        '    },',
        '  },',
        '}];',
        '',
        `const messages = [{ role: "user", content: "${question}" }];`,
        `const response = await client.chat.completions.create({ model: "${model}", messages, tools });`,
        'const message = response.choices[0].message;',
        '',
        'if (message.tool_calls) {',
        '  const call = message.tool_calls[0];',
        '  const hasil = cekOngkir(JSON.parse(call.function.arguments)); // fungsi Anda',
        '  messages.push(message, { role: "tool", tool_call_id: call.id, content: JSON.stringify(hasil) });',
        `  const final = await client.chat.completions.create({ model: "${model}", messages, tools });`,
        '  console.log(final.choices[0].message.content);',
        '}',
      ].join('\n');
    case 'php':
      return [
        ...clientHeader('php', key),
        '$tools = [[',
        "    'type' => 'function',",
        "    'function' => [",
        "        'name' => 'cek_ongkir',",
        "        'description' => 'Hitung ongkos kirim antar kota',",
        "        'parameters' => [",
        "            'type' => 'object',",
        "            'properties' => [",
        "                'asal' => ['type' => 'string'],",
        "                'tujuan' => ['type' => 'string'],",
        "                'berat_kg' => ['type' => 'number'],",
        '            ],',
        "            'required' => ['asal', 'tujuan', 'berat_kg'],",
        '        ],',
        '    ],',
        ']];',
        '',
        `$messages = [['role' => 'user', 'content' => '${question}']];`,
        `$response = $client->chat()->create(['model' => '${model}', 'messages' => $messages, 'tools' => $tools]);`,
        '$call = $response->choices[0]->message->toolCalls[0] ?? null;',
        '',
        'if ($call) {',
        '    $hasil = cek_ongkir(json_decode($call->function->arguments, true)); // fungsi Anda',
        "    $messages[] = ['role' => 'assistant', 'content' => null, 'tool_calls' => [$call->toArray()]];",
        "    $messages[] = ['role' => 'tool', 'tool_call_id' => $call->id, 'content' => json_encode($hasil)];",
        `    $final = $client->chat()->create(['model' => '${model}', 'messages' => $messages, 'tools' => $tools]);`,
        '    echo $final->choices[0]->message->content;',
        '}',
      ].join('\n');
  }
}

/** Structured output via json_schema. */
export function jsonSnippet(lang: Lang, options: DocSnippetOptions = {}): string {
  const model = options.model ?? DEFAULT_MODEL;
  const key = options.apiKey;
  const text = 'Ekstrak data: Budi Santoso, 34 tahun, tinggal di Surabaya.';
  switch (lang) {
    case 'curl':
      return [
        `curl ${BASE_URL}/chat/completions \\`,
        `  -H "Authorization: Bearer ${key ?? `$${ENV}`}" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -d '{`,
        `    "model": "${model}",`,
        `    "messages": [{"role": "user", "content": "${text}"}],`,
        `    "response_format": {`,
        `      "type": "json_schema",`,
        `      "json_schema": {`,
        `        "name": "pelanggan",`,
        `        "strict": true,`,
        `        "schema": {`,
        `          "type": "object",`,
        `          "properties": {`,
        `            "nama": {"type": "string"},`,
        `            "umur": {"type": "integer"},`,
        `            "kota": {"type": "string"}`,
        `          },`,
        `          "required": ["nama", "umur", "kota"],`,
        `          "additionalProperties": false`,
        `        }`,
        `      }`,
        `    }`,
        `  }'`,
      ].join('\n');
    case 'python':
      return [
        ...clientHeader('python', key, ['json']),
        'response = client.chat.completions.create(',
        `    model="${model}",`,
        `    messages=[{"role": "user", "content": "${text}"}],`,
        '    response_format={',
        '        "type": "json_schema",',
        '        "json_schema": {',
        '            "name": "pelanggan",',
        '            "strict": True,',
        '            "schema": {',
        '                "type": "object",',
        '                "properties": {',
        '                    "nama": {"type": "string"},',
        '                    "umur": {"type": "integer"},',
        '                    "kota": {"type": "string"},',
        '                },',
        '                "required": ["nama", "umur", "kota"],',
        '                "additionalProperties": False,',
        '            },',
        '        },',
        '    },',
        ')',
        'data = json.loads(response.choices[0].message.content)',
        'print(data["nama"], data["kota"])',
      ].join('\n');
    case 'node':
      return [
        ...clientHeader('node', key),
        'const response = await client.chat.completions.create({',
        `  model: "${model}",`,
        `  messages: [{ role: "user", content: "${text}" }],`,
        '  response_format: {',
        '    type: "json_schema",',
        '    json_schema: {',
        '      name: "pelanggan",',
        '      strict: true,',
        '      schema: {',
        '        type: "object",',
        '        properties: { nama: { type: "string" }, umur: { type: "integer" }, kota: { type: "string" } },',
        '        required: ["nama", "umur", "kota"],',
        '        additionalProperties: false,',
        '      },',
        '    },',
        '  },',
        '});',
        'const data = JSON.parse(response.choices[0].message.content);',
        'console.log(data.nama, data.kota);',
      ].join('\n');
    case 'php':
      return [
        ...clientHeader('php', key),
        '$response = $client->chat()->create([',
        `    'model' => '${model}',`,
        `    'messages' => [['role' => 'user', 'content' => '${text}']],`,
        "    'response_format' => [",
        "        'type' => 'json_schema',",
        "        'json_schema' => [",
        "            'name' => 'pelanggan',",
        "            'strict' => true,",
        "            'schema' => [",
        "                'type' => 'object',",
        "                'properties' => [",
        "                    'nama' => ['type' => 'string'],",
        "                    'umur' => ['type' => 'integer'],",
        "                    'kota' => ['type' => 'string'],",
        '                ],',
        "                'required' => ['nama', 'umur', 'kota'],",
        "                'additionalProperties' => false,",
        '            ],',
        '        ],',
        '    ],',
        ']);',
        '$data = json_decode($response->choices[0]->message->content, true);',
        "echo $data['nama'] . ', ' . $data['kota'];",
      ].join('\n');
  }
}
